/**
 * "Did you mean Essaouira?" (owner, 2026-10-26, idea 77 in the suggestions
 * lab).
 *
 * Place names are easy to misspell, and a search that answers "esauira" with
 * nothing reads as "we do not go there". This finds the place the guest most
 * likely meant and rewrites the query with it, so the search can show that
 * place's results and say so. Both the website's header search and the app's
 * search screen use it, so they correct the same words the same way.
 *
 * Deliberately narrow: only words of four letters or more, only against the
 * place names passed in, and only when the word is not already part of one.
 * The search keeps its own one-letter tolerance; this is for the misspelling
 * that is two letters off, which that tolerance does not reach.
 */

export type DidYouMeanPlace = {
  name: string;
  /** How much the place has, to break a tie between two equally close names. */
  weight?: number;
};

export type DidYouMean = {
  /** The query with the misspelled word replaced: "quad essaouira". */
  query: string;
  /** The place it was corrected to, as the place spells itself: "Essaouira". */
  place: string;
};

/** Lowercase with accents stripped, so "Fès" and "fes" compare equal. */
function fold(value: string): string {
  return value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

/**
 * Optimal string alignment distance (Levenshtein plus swapping two neighbours),
 * stopping early once it can no longer come in under `max`.
 */
export function editDistance(a: string, b: string, max = Number.POSITIVE_INFINITY): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const rows = a.length + 1;
  const cols = b.length + 1;
  const d: number[][] = Array.from({ length: rows }, () => new Array<number>(cols).fill(0));
  for (let i = 0; i < rows; i += 1) d[i]![0] = i;
  for (let j = 0; j < cols; j += 1) d[0]![j] = j;
  for (let i = 1; i < rows; i += 1) {
    let rowMin = Number.POSITIVE_INFINITY;
    for (let j = 1; j < cols; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let v = Math.min(d[i - 1]![j]! + 1, d[i]![j - 1]! + 1, d[i - 1]![j - 1]! + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        v = Math.min(v, d[i - 2]![j - 2]! + 1);
      }
      d[i]![j] = v;
      if (v < rowMin) rowMin = v;
    }
    if (rowMin > max) return max + 1;
  }
  return d[a.length]![b.length]!;
}

/** How many letters a word of this length may be off and still be corrected. */
function allowedEdits(length: number): number {
  if (length < 4) return 0;
  if (length <= 5) return 1;
  return 2;
}

/**
 * The query with its misspelled place name corrected, or null when nothing
 * needs correcting (or nothing is close enough to be sure).
 */
export function didYouMean(query: string, places: readonly DidYouMeanPlace[]): DidYouMean | null {
  const words = query.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0 || places.length === 0) return null;

  const folded = places
    .filter((p) => p.name && p.name.trim())
    .map((p) => ({ place: p, name: fold(p.name.trim()), weight: p.weight ?? 0 }));

  let changed = false;
  let corrected: string | null = null;
  const out = words.map((word) => {
    const w = fold(word);
    const max = allowedEdits(w.length);
    if (max === 0) return word;
    // Already part of a place name ("marra", "chef"): the search finds it.
    if (folded.some((p) => p.name.includes(w) || p.name.split(/[\s-]+/).some((part) => part.startsWith(w)))) {
      return word;
    }
    let best: { place: DidYouMeanPlace; distance: number; weight: number } | null = null;
    for (const p of folded) {
      // Compare with the whole name and with each of its words, so "chaouen"
      // can still reach a two-word name.
      const candidates = [p.name, ...p.name.split(/[\s-]+/).filter((part) => part.length >= 4)];
      for (const c of candidates) {
        const distance = editDistance(w, c, max);
        if (distance === 0 || distance > max) continue;
        if (
          !best ||
          distance < best.distance ||
          (distance === best.distance && p.weight > best.weight)
        ) {
          best = { place: p.place, distance, weight: p.weight };
        }
      }
    }
    if (!best || corrected) return word;
    changed = true;
    corrected = best.place.name.trim();
    return corrected;
  });

  if (!changed || !corrected) return null;
  return { query: out.join(" "), place: corrected };
}
